<template>

  <div>
    <Modal
      title="Edit Default Rate"
      :submit="submit"
      :submitting="submitting"
    >

      <o-select
        label="Product"
        required
        v-model="v$.locationProductID.$model"
        :validator="v$.locationProductID"
        :items="locationProductOptions"
      />

      <RateSet
        v-if="formState.locationProductID"
        v-model="formState.rates"
        :location="location"
        :tiered="isTiered"
        :readonly="false"
        :max-tier-duration-in-hours="isTiered ? maxTierDurationInHours : null"
      />

    </Modal>
  </div>

</template>

<script setup>

import { computed, onBeforeMount, reactive, ref, watch } from 'vue'

// components
import RateSet from '../../../../RateSet.vue'

// models
import { ChannelFacility } from '../../../../../models/ChannelFacility'
import { DefaultRate } from '../../../../../models/DefaultRate'
import Location from '../../../../../models/Location'
import LocationProduct from '../../../../../models/LocationProduct'
import Product from '../../../../../models/Product'

// validation
import useVuelidate from '@vuelidate/core'
import { required } from '../../../../../utils/i18n-validators'

// misc
import { buildRateSetFormState, saveRateSetFormState } from '../../../../../utils/rateSetUtils'
import { closeDialog } from 'vue3-promise-dialog'
import NotificationService from '../../../../../services/NotificationService'
import VerticalRegistry from '../../../../../verticals/VerticalRegistry'
import { useProductMaxDuration } from '../../../../../utils/useProductMaxDuration'

const props = defineProps({

  // We assume that ChannelFacilities for this location have already been
  // loaded into the store
  location: {
    type: Location,
    required: true,
  },

  // We assume that this LP has already been loaded into the store
  locationProductID: {
    type: Number,
    required: false,
  },

})

const locationProductOptions = computed(() => {
  
  const locationProducts = LocationProduct.query()
    .with(['product'])
    .where('locationID', props.location.ID)
    .whereHasNot('product', (query) => {
      query.where('verticalType', (verticalType) => ['event', 'spothero_iq'].includes(verticalType))
    })
    .get()

  return locationProducts
    .sort((lp1, lp2) => {
      // We can't use orderBy() to do this sorting in the query, because Vuex
      // passes the LP to that function without the product relation.
      const verticals = ['airport', 'monthly', 'transient', 'event', 'on_demand']
      const lp1Order = lp1.product ? verticals.indexOf(lp1.product.verticalType) : 0
      const lp2Order = lp2.product ? verticals.indexOf(lp2.product.verticalType) : 0
      if (lp1Order > lp2Order) {
        return 1
      }
      if (lp1Order < lp2Order) {
        return -1
      }
      return 0
    })
    .map((locationProduct) => {
      return {
        title: locationProduct.product ? locationProduct.product.name : '(unknown)',
        value: locationProduct.ID,
      }
    })

})

const isTiered = computed(() => {
  return VerticalRegistry.get(selectedProduct.value.verticalType)?.supports(['tieredRates']) ?? false
})

const originalDefaultRateIDs = ref([])

/**
 * Form state and rules
 */

const formState = reactive({
  locationProductID: null,
  rates: {
    defaultRates: [],
  },
})

const formRules = {
  locationProductID: { required },
}

const v$ = useVuelidate(formRules, formState)

const selectedLocationProduct = computed(() => {
  return LocationProduct.query().with(['product']).whereId(formState.locationProductID).first()
})

const selectedProduct = computed(() => {
  return Product.find(selectedLocationProduct.value.productID)
})

const { getComputedsForStrings } = useProductMaxDuration()

const { maxTierDurationInHours } = getComputedsForStrings(
  computed(() => selectedLocationProduct.value?.firstStartTime ?? selectedProduct.value?.firstStartTime ?? null),
  computed(() => selectedLocationProduct.value?.lastStartTime ?? selectedProduct.value?.lastStartTime ?? null),
  computed(() => selectedLocationProduct.value?.endTime ?? selectedProduct.value?.endTime ?? null),
)

/**
 * Initialization
 */

let channelFacilities = []

onBeforeMount(() => {
  channelFacilities = ChannelFacility.query().where('facilityID', props.location.facilityID).all()
  formState.locationProductID = props.locationProductID
  buildFormState()
})

function buildFormState() {

  const validCdrStatuses = ['created', 'pending update', 'updated', 'failed update']

  const defaultRates = DefaultRate.query()
    .whereHas('channelDefaultRates', (query) => {
      query.where('status', (value) => validCdrStatuses.includes(value))
    })
    .with('channelDefaultRates')
    .where('locationProductID', formState.locationProductID)
    .all()

  let lpChannelFacilities = []
  if (selectedLocationProduct.value) {
    lpChannelFacilities = channelFacilities.filter((channelFacility) => {
      return selectedLocationProduct.value.channelFacilityIDs.includes(channelFacility.ID)
    })
  }
  formState.rates.defaultRates = buildRateSetFormState(defaultRates, lpChannelFacilities)
  originalDefaultRateIDs.value = defaultRates.map((defaultRate) => defaultRate.ID)
}

watch(() => formState.locationProductID, () => {
  buildFormState()
})

/**
 * Submission
 */

const submitting = ref(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  saveRateSetFormState(props.location.ID, formState.locationProductID, formState.rates.defaultRates, originalDefaultRateIDs.value)
    .then(() => {
      NotificationService.success('Default rates updated')
      closeDialog(true)
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error('Couldn\'t update default rates!')
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
